import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { HiveImpasse, HiveThought as Thought } from "~/api/aiAdminTypes";
import { MessageMarkdown } from "~/features/conversations/MessageMarkdown";
import { Button } from "~/foundation/components/Button";
import { Input } from "~/foundation/components/Input";
import { Text } from "~/foundation/components/Text";
import { cn } from "~/foundation/utils/cn";

/** Your call on one impasse, as the UI holds it before the re-run. */
export interface RulingDraft {
  for: string;
  note: string;
}

const percent = (value: number) => `${Math.round(value * 100)}%`;

/**
 * THE THOUGHT — what happened inside a thinker: every facet's view, the impasses with their
 * votes, the authority's decision. Shared by Try it (where unresolved impasses take YOUR CALL)
 * and the research room's seat panel (read-only).
 */
export function HiveThought({
  name,
  thought,
  rulings,
  onRule,
  onApplyRulings,
  applying = false,
}: {
  name: string;
  thought: Thought;
  /** Present in Try it: the owner can rule on unresolved impasses. */
  rulings?: Record<string, RulingDraft>;
  onRule?: (impasseId: string, draft: RulingDraft | null) => void;
  onApplyRulings?: () => void;
  applying?: boolean;
}) {
  const { t } = useTranslation();
  const facetName = (id: string) => thought.views.find((v) => v.facetId === id)?.name ?? id;
  const unresolved = thought.impasses.filter((i) => i.status === "unresolved");
  const ruled = rulings ? Object.keys(rulings).length : 0;
  return (
    <section className="flex flex-col gap-3" aria-label={t("Inside {{name}}", { name })}>
      <div>
        <h5 className="text-small font-medium text-text">{t("Inside {{name}}", { name })}</h5>
        <Text variant="small">
          {t(
            "{{facets}} facets answered · {{impasses}} impasses · {{calls}} calls · {{seconds}} s",
            {
              facets: thought.views.length,
              impasses: thought.impasses.length,
              calls: thought.calls,
              seconds: Math.round(thought.durationMs / 1000),
            },
          )}
        </Text>
      </div>

      <ul className="grid gap-2 sm:grid-cols-2" aria-label={t("Facet views")}>
        {thought.views.map((view) => (
          <li
            key={view.facetId}
            className={cn(
              "flex flex-col gap-1 rounded border border-divider p-2 text-small",
              !view.valid && "border-dashed opacity-80",
            )}
            aria-label={view.name}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-text">{view.name}</span>
              <span className="text-text-tertiary">
                {t("weight {{weight}}", { weight: percent(view.weight) })} ·{" "}
                {t("confidence {{confidence}}", { confidence: percent(view.confidence) })}
              </span>
            </div>
            {view.position ? (
              <p className="text-text-secondary">
                {t("Position")}: {view.position}
              </p>
            ) : null}
            <p className="whitespace-pre-wrap text-text">{view.view}</p>
            {view.objections.map((objection) => (
              <p key={`${objection.to}:${objection.claim}`} className="text-text-secondary">
                → {t("objects to {{name}}", { name: facetName(objection.to) })}: {objection.claim}
                {objection.why ? ` — ${objection.why}` : ""}
              </p>
            ))}
            {!view.valid ? (
              <p className="text-text-tertiary">{t("This view did not come back usable.")}</p>
            ) : null}
          </li>
        ))}
      </ul>

      {thought.impasses.length > 0 ? (
        <section className="flex flex-col gap-2" aria-label={t("Impasses")}>
          <h5 className="text-small font-medium text-text">{t("Impasses")}</h5>
          <ul className="flex flex-col gap-2">
            {thought.impasses.map((impasse) => (
              <ImpasseCard
                key={impasse.id}
                impasse={impasse}
                facetName={facetName}
                ruling={rulings?.[impasse.id]}
                onRule={onRule}
              />
            ))}
          </ul>
          {onApplyRulings && unresolved.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="primary"
                disabled={ruled === 0 || applying}
                onClick={onApplyRulings}
              >
                {t("Re-run with your rulings")}
              </Button>
              <Text variant="small">
                {ruled === 0
                  ? t("The hive could not settle {{count}} of these. Your call decides them.", {
                      count: unresolved.length,
                    })
                  : t("{{ruled}} of {{count}} ruled; the thinker thinks again with your rulings.", {
                      ruled,
                      count: unresolved.length,
                    })}
              </Text>
            </div>
          ) : null}
        </section>
      ) : null}

      {thought.decision ? (
        <section
          className="flex flex-col gap-1 rounded border border-divider bg-bg-secondary/60 p-3"
          aria-label={t("The decision")}
        >
          <h5 className="text-small font-medium text-text">
            {t("The decision — {{authority}}", { authority: thought.decision.authorityName })}
          </h5>
          <MessageMarkdown content={thought.decision.answer || "…"} />
          <p className="text-small text-text-secondary">
            {thought.decision.tookFrom.length > 0
              ? t("took from: {{names}}", {
                  names: thought.decision.tookFrom.map(facetName).join(", "),
                })
              : t("took from no single facet")}
            {thought.decision.dissent.length > 0
              ? ` · ${t("dissent: {{names}}", {
                  names: thought.decision.dissent.map(facetName).join(", "),
                })}`
              : ""}
          </p>
        </section>
      ) : null}
    </section>
  );
}

function ImpasseCard({
  impasse,
  facetName,
  ruling,
  onRule,
}: {
  impasse: HiveImpasse;
  facetName: (id: string) => string;
  ruling?: RulingDraft;
  onRule?: (impasseId: string, draft: RulingDraft | null) => void;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState(ruling?.note ?? "");
  const sides = [impasse.from, impasse.to];
  const result =
    impasse.status === "ruled"
      ? impasse.resolution || t("The owner ruled.")
      : impasse.status === "resolved"
        ? impasse.resolution ||
          t("Settled by the votes: {{winner}} holds.", {
            winner: facetName(impasse.forFrom >= impasse.forTo ? impasse.from : impasse.to),
          })
        : t("Unresolved after {{rounds}} rounds — the hive could not settle it.", {
            rounds: impasse.rounds,
          });
  return (
    <li
      className={cn(
        "flex flex-col gap-1.5 rounded border p-2 text-small",
        impasse.status === "unresolved" ? "border-amber-500/60" : "border-divider",
      )}
      aria-label={impasse.claim}
    >
      <p className="font-medium text-text">{impasse.claim}</p>
      <p className="text-text-secondary">
        {facetName(impasse.from)} {t("vs")} {facetName(impasse.to)}
        {impasse.why ? ` — ${impasse.why}` : ""}
      </p>
      {impasse.votes.length > 0 ? (
        <ul className="flex flex-col gap-0.5" aria-label={t("Votes")}>
          {impasse.votes.map((vote) => (
            <li key={`${vote.facetId}:${vote.for}`} className="text-text-secondary">
              {vote.name} ({percent(vote.weight)}) → {facetName(vote.for)}
              {vote.why ? `: ${vote.why}` : ""}
            </li>
          ))}
        </ul>
      ) : null}
      <p className="text-text-secondary">
        {t("{{from}} {{forFrom}} · {{to}} {{forTo}}", {
          from: facetName(impasse.from),
          forFrom: percent(impasse.forFrom),
          to: facetName(impasse.to),
          forTo: percent(impasse.forTo),
        })}
      </p>
      <p
        className={cn(
          "font-medium",
          impasse.status === "unresolved" ? "text-amber-600" : "text-text",
        )}
      >
        {result}
      </p>
      {impasse.status === "unresolved" && onRule ? (
        <div className="flex flex-col gap-1.5 rounded border border-dashed border-divider p-2">
          <span className="font-medium text-text">{t("Your call")}</span>
          <div className="flex flex-wrap gap-1.5">
            {[...sides, "neither"].map((side) => {
              const picked = ruling?.for === side;
              return (
                <Button
                  key={side}
                  size="sm"
                  variant={picked ? "primary" : "neutral"}
                  aria-pressed={picked}
                  onClick={() => onRule(impasse.id, picked ? null : { for: side, note })}
                >
                  {side === "neither" ? t("Neither") : facetName(side)}
                </Button>
              );
            })}
          </div>
          <Input
            label={t("Why (optional)")}
            value={note}
            fullWidth
            placeholder={t("A sentence the thinker will take into account")}
            onChange={(event) => {
              setNote(event.target.value);
              if (ruling) onRule(impasse.id, { ...ruling, note: event.target.value });
            }}
          />
        </div>
      ) : null}
    </li>
  );
}
